import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { NsEmpWebExpData } from "../../../models/emp-web-exp.model";
import { CONSTANTS_ASSETS_EMP_WEB_EXP } from "../../../constants/assets.constants";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { switchMap, take, takeUntil } from "rxjs/operators";
import { ApiService } from "../../../services/api.service";
import { CommuteService } from "../../../services/commute.service";
import { LoaderService } from "../../../services/loader.service";
import { ConfigurationService } from "../../../services/configuration.service";
import { ToastService } from "mis-crystal-design-system/toast";

@Component({
  selector: "[mis-employee-web-exp-parking]",
  templateUrl: "./parking.component.html",
  styleUrls: ["./parking.component.scss"]
})
export class ParkingComponent implements OnInit, OnDestroy {
  constructor(
    public commuteSvc: CommuteService,
    private loader: LoaderService,
    private configSvc: ConfigurationService,
    private toasts: ToastService
  ) {
    this.parkingForm = this.buildForm();
  }
  readonly Assets = CONSTANTS_ASSETS_EMP_WEB_EXP;
  premises: NsEmpWebExpData.IParkingPremise[] = [];
  premiseZones: NsEmpWebExpData.IParkingPremise[] = [];
  vehicles: NsEmpWebExpData.IParkingPremise[] = [];
  zones: NsEmpWebExpData.IParkingPremise[] = [];
  levels: NsEmpWebExpData.IParkingPremise[] = [];
  configs = this.configSvc.bookingConfigurations;
  parkingForm = undefined;
  private endObs = new Subject();
  enableParkingFloorPlan = false;

  ngOnInit(): void {
    this.enableParkingFloorPlan = this.commuteSvc.enableGridFloorPlan;
    this.parkingForm.controls.vehicle.valueChanges.subscribe(vehicleType => {
      if (vehicleType) this.commuteSvc.setAllotedParkingData({ vehicleType: vehicleType?.premiseType, zoneId: null, levelId: null, seatId: null });
    });
    this.commuteSvc.parkingRefreshStream
      .pipe(
        takeUntil(this.endObs),
        switchMap(obs => {
          this.loader.show();
          return obs;
        })
      )
      .subscribe(res => {
        this.premises = [...res];
        this.premiseZones = [...res.map(p => ({ ...p, label: p.parentPremiseName }))];
        const vehicleTypes = this.groupBy("premiseType", res);
        this.vehicles = Array.from(vehicleTypes.values());
        this.parkingForm.patchValue({ vehicle: this.vehicles[0] });
        this.commuteSvc.formData.pipe(take(1)).subscribe(([, , { login }]) => {
          if (login?.id === "parking") {
            const level = this.premises.find(prm => prm.premiseId === login.value.premiseId);
            if (level) {
              const vehicle = this.vehicles.find(prm => prm.premiseType === level.premiseType);
              !this.enableParkingFloorPlan && this.setZones(vehicle);
              const zone = this.premiseZones.find(prm => prm.parentPremise === level.parentPremise);
              const parkingData: NsEmpWebExpData.IAllotedParkingDetails = {
                vehicleType: zone.premiseType,
                zoneId: level.parentPremise,
                levelId: level.premiseId
              };
              this.commuteSvc.setAllotedParkingData(parkingData);
              !this.enableParkingFloorPlan && this.setLevels(zone);
              this.parkingForm.patchValue({ vehicle, zone, level }, { emitEvent: false, onlySelf: true });
              const opt: [NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?] = [
                { id: "parking", value: level, disable: false },
                undefined
              ];
              if (this.configSvc.isMobile) {
                this.commuteSvc.temporaryCommuteSelection = opt;
                return;
              }
              this.commuteSvc.setCommute(opt);
            }
          }
        });
        this.loader.hide();
      });
    if (!this.enableParkingFloorPlan) {
      this.listenFormChanges();
    }
    this.commuteSvc.regNo.valueChanges.pipe(takeUntil(this.endObs)).subscribe(res => {
      if (res) {
        const opt: [NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?] = [
          this.parkingForm.value.level ? { id: "parking", value: this.parkingForm.value.level, disable: false } : undefined,
          undefined,
          res
        ];
        if (this.configSvc.isMobile) {
          this.commuteSvc.temporaryCommuteSelection = opt;
          return;
        }
        this.commuteSvc.setCommute(opt);
      }
    });
  }

  ngOnDestroy(): void {
    this.endObs.next();
    this.endObs.complete();
  }

  private listenFormChanges(): void {
    const vehicleCtrl = this.parkingForm.get("vehicle");
    const zoneCtrl = this.parkingForm.get("zone");
    vehicleCtrl.valueChanges.pipe(takeUntil(this.endObs)).subscribe(type => {
      this.setZones(type);
      this.commuteSvc.regNo.patchValue("");
    });
    zoneCtrl.valueChanges.pipe(takeUntil(this.endObs)).subscribe(type => {
      this.setLevels(type);
    });
  }

  private groupBy<T>(key: string, arr: T[]): Map<string, T> {
    const res = new Map();
    for (const item of arr) {
      if (!res.has(item[key])) {
        res.set(item[key], item);
      }
    }
    return res;
  }

  private buildForm(): FormGroup {
    const form = new FormGroup({
      vehicle: new FormControl({}, [Validators.required]),
      regNo: new FormControl("", []),
      zone: new FormControl(undefined, [Validators.required]),
      level: new FormControl(undefined, [Validators.required])
    });
    return form;
  }

  private setZones(type: NsEmpWebExpData.IParkingPremise): void {
    this.zones = Array.from(this.groupBy("parentPremise", this.premiseZones)?.values())
      .filter(premise => premise.premiseType === type?.premiseType)
      .sort((a, b) => a.parentPremiseName.localeCompare?.(b.parentPremiseName));
    this.setPremise("zone", this.zones[0]);
  }

  private setLevels(type: NsEmpWebExpData.IParkingPremise): void {
    this.levels = this.premises.filter(premise => premise.parentPremise === type?.parentPremise).sort((a, b) => a.premise.localeCompare?.(b.premise));
    this.setPremise("level", this.levels.filter(level => level.availability > 0)[0]);
  }

  setPremise(ctrl: string, value: NsEmpWebExpData.IParkingPremise): void {
    if (ctrl === "level" && value?.availability < 1) {
      this.toasts.displayMsg("Parking slots are not available!");
      return;
    }
    this.parkingForm.patchValue({ [ctrl]: value });
    if (ctrl === "level") {
      const opt: [NsEmpWebExpData.ICommuteOption?, NsEmpWebExpData.ICommuteOption?, string?] = [
        value ? { id: "parking", value, disable: false } : undefined,
        undefined
      ];
      if (this.configSvc.isMobile) {
        this.commuteSvc.temporaryCommuteSelection = opt;
        return;
      }
      this.commuteSvc.setCommute(opt);
    }
  }
}
